import { useQuery } from "@tanstack/react-query";
import { CircleAlert, CircleCheck, CircleHelp, Moon, RefreshCw, Sun } from "lucide-react";
import { fetchHealth } from "@/lib/api";
import type { HealthResponse } from "@/lib/api/types";
import { Pill } from "@/components/ui/Pill";
import { useTheme } from "@/lib/theme";

const STATUS_TONE: Record<HealthResponse["status"], "good" | "warn" | "bad"> = {
  ok: "good",
  degraded: "warn",
  down: "bad",
};

export function TopBar() {
  const { theme, toggle } = useTheme();
  const { data, isError, refetch, isFetching } = useQuery({
    queryKey: ["health"],
    queryFn: fetchHealth,
    refetchInterval: 30_000,
  });

  const Icon = isError
    ? CircleAlert
    : data?.status === "ok"
      ? CircleCheck
      : data?.status === "degraded"
        ? CircleAlert
        : CircleHelp;

  const tone = isError ? "bad" : data ? STATUS_TONE[data.status] : "warn";
  const label = isError
    ? "API unreachable"
    : data
      ? `${data.status} · v${data.version} · ${data.environment}`
      : "checking";

  return (
    <header className="flex h-12 items-center justify-between border-b border-border bg-surface-raised px-4">
      <div className="flex items-center gap-2">
        <Pill tone={tone}>
          <Icon size={12} className="mr-1" />
          {label}
        </Pill>
        {data?.components && (
          <span className="hidden text-2xs text-foreground-muted lg:inline">
            {data.components.map((c) => `${c.name}:${c.status}`).join(" · ")}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={toggle}
          className="rounded-md p-1.5 text-foreground-secondary hover:bg-surface-subtle hover:text-foreground-primary"
          title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        >
          {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
        </button>
        <button
          type="button"
          onClick={() => refetch()}
          className="rounded-md p-1.5 text-foreground-secondary hover:bg-surface-subtle hover:text-foreground-primary"
          title="Refresh status"
          aria-label="Refresh status"
        >
          <RefreshCw size={14} className={isFetching ? "animate-spin" : ""} />
        </button>
      </div>
    </header>
  );
}
